import React, { useState } from 'react';
import { Box, Button, IconButton, Menu, MenuItem, ListItemText, CircularProgress } from '@mui/material';
import { ArrowDropDown, MoreHoriz, CloudSync, OpenInNew, CategoryOutlined } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { CameraWithPricing } from '../../utils/productDetailHelpers';
import { manufacturerSiteUrl } from '../../utils/manufacturerSiteUrl';
import { tokens } from '../../theme/tokens';
import type { ErpResyncOptions } from '../../services/api';
import { useOrgFeatureFlag } from '../../hooks/useOrgFeatureFlags';
import { useAppTranslation } from '../../i18n/useAppTranslation';

const outlinedActionSx = {
  height: 38,
  px: '14px',
  borderRadius: '10px',
  border: '1px solid',
  borderColor: 'divider',
  bgcolor: 'background.paper',
  color: 'text.primary',
  textTransform: 'none',
  fontWeight: 600,
  fontSize: '13.5px',
  '&:hover': { borderColor: tokens.ink.disabled, bgcolor: 'background.paper' },
} as const;

const RESYNC_OPTIONS: Array<{ label: string; help: string; opts: ErpResyncOptions }> = [
  { label: 'Force resync', help: 'Skips the “already up to date” check. Pushes the price when sync pricing is on.', opts: {} },
  { label: 'Force price', help: 'Push this product’s price even when sync pricing is off. Titles and photos stay as they are.', opts: { forcePricing: true } },
  { label: 'Force description', help: 'Overwrite listing copy with the catalog text.', opts: { forceDescription: true } },
  { label: 'Force images', help: 'Replace store photos with the catalog gallery.', opts: { forceImages: true } },
  { label: 'Force description & images', help: 'Both — a full content overwrite of the listing.', opts: { forceDescription: true, forceImages: true } },
];

interface ProductActionsRowProps {
  product: CameraWithPricing;
  inSync: boolean;
  isStoreScoped: boolean;
  isVendor: boolean;
  isPlatformAdmin: boolean;
  onAddToSync: () => void;
  addToSyncPending: boolean;
  onForceResync: (opts?: ErpResyncOptions) => void;
  forceResyncPending: boolean;
  onRemoveFromSync: () => void;
  removeFromSyncPending: boolean;
  onViewSyncSelection: () => void;
}

/**
 * Primary action + overflow menu, per the design's "eleven buttons → three
 * plus a menu": not-yet-selected products get a single "Add to sync"; once
 * selected, the primary becomes a "Force resync" split-button (the resync
 * flag combos), and "Remove from sync" moves into the overflow rather than
 * sitting at the same weight as everything else.
 */
export function ProductActionsRow({
  product,
  inSync,
  isStoreScoped,
  isVendor,
  isPlatformAdmin,
  onAddToSync,
  addToSyncPending,
  onForceResync,
  forceResyncPending,
  onRemoveFromSync,
  removeFromSyncPending,
  onViewSyncSelection,
}: ProductActionsRowProps) {
  const navigate = useNavigate();
  const { t } = useAppTranslation();
  const { isAdmin } = useAuth();
  const { enabled: lightspeedConfigOn } = useOrgFeatureFlag('lightspeed_store_config');
  const forceCategoryOption = {
    label: t('integrations.lightspeedConfig.forceCategory.label'),
    help: t('integrations.lightspeedConfig.forceCategory.help'),
    opts: { forceCategories: true } as ErpResyncOptions,
  };
  const [resyncAnchor, setResyncAnchor] = useState<null | HTMLElement>(null);
  const [moreAnchor, setMoreAnchor] = useState<null | HTMLElement>(null);

  const showSplitButton = isStoreScoped && inSync;
  const showAddToSync = isStoreScoped && !inSync;

  const moreItems: Array<{ label: string; onClick: () => void; danger?: boolean; disabled?: boolean }> = [];
  if (isStoreScoped) moreItems.push({ label: 'View Sync Selection', onClick: onViewSyncSelection });
  if (isVendor) {
    moreItems.push({ label: 'MAP Management', onClick: () => navigate('/app/vendor-map-management') });
    moreItems.push({ label: 'Price History', onClick: () => navigate('/app/price-history') });
  }
  if (isStoreScoped && inSync) {
    // Disabled while the removal is in flight — the menu can be reopened, and
    // a second click would fire a duplicate mutation.
    moreItems.push({ label: 'Remove From Sync', onClick: onRemoveFromSync, danger: true, disabled: removeFromSyncPending });
  }

  const manufacturerUrl = manufacturerSiteUrl(product);

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
      {showAddToSync && (
        <Button
          onClick={onAddToSync}
          disabled={addToSyncPending}
          startIcon={addToSyncPending ? <CircularProgress size={14} sx={{ color: 'common.white' }} /> : <CloudSync sx={{ fontSize: 16 }} />}
          sx={{
            height: 38,
            px: '14px',
            borderRadius: '10px',
            bgcolor: 'primary.main',
            color: 'common.white',
            textTransform: 'none',
            fontWeight: 600,
            fontSize: '13.5px',
            '&:hover': { bgcolor: 'primary.dark' },
          }}
        >
          Add To Sync
        </Button>
      )}

      {showSplitButton && (
        <Box sx={{ position: 'relative', display: 'inline-flex', alignItems: 'stretch', height: 38, borderRadius: '10px', bgcolor: 'primary.main', overflow: 'visible' }}>
          <Button
            onClick={() => onForceResync()}
            disabled={forceResyncPending}
            startIcon={forceResyncPending ? <CircularProgress size={14} sx={{ color: 'common.white' }} /> : <CloudSync sx={{ fontSize: 16 }} />}
            sx={{
              height: 38,
              px: '14px',
              minWidth: 0,
              borderRadius: '10px 0 0 10px',
              bgcolor: 'transparent',
              color: 'common.white',
              textTransform: 'none',
              fontWeight: 600,
              fontSize: '13.5px',
              '&:hover': { bgcolor: 'primary.dark' },
            }}
          >
            {/* One flex item: MUI Button is inline-flex, so a bare text node beside
                a span became two items and the leading space of " to store"
                collapsed ("Force resyncto store"). */}
            <Box component="span" sx={{ whiteSpace: 'nowrap' }}>
              Force resync
              <Box component="span" sx={{ display: { xs: 'none', sm: 'inline' } }}>{' to store'}</Box>
            </Box>
          </Button>
          <IconButton
            aria-label="Force resync options"
            onClick={(e) => setResyncAnchor(e.currentTarget)}
            sx={{
              width: 32,
              flex: '0 0 auto',
              borderRadius: '0 10px 10px 0',
              borderLeft: '1px solid rgba(255,255,255,0.28)',
              bgcolor: resyncAnchor ? 'primary.dark' : 'transparent',
              color: 'common.white',
              '&:hover': { bgcolor: 'primary.dark' },
            }}
          >
            <ArrowDropDown sx={{ fontSize: 18 }} />
          </IconButton>
          <Menu
            anchorEl={resyncAnchor}
            open={Boolean(resyncAnchor)}
            onClose={() => setResyncAnchor(null)}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
            transformOrigin={{ vertical: 'top', horizontal: 'left' }}
            PaperProps={{ sx: { width: 330, borderRadius: '12px', border: '1px solid', borderColor: 'divider', boxShadow: '0 12px 32px rgba(15,23,42,0.14)' } }}
          >
            {[...RESYNC_OPTIONS, ...(lightspeedConfigOn ? [forceCategoryOption] : [])].map((opt) => (
              <MenuItem
                key={opt.label}
                disabled={forceResyncPending}
                onClick={() => {
                  setResyncAnchor(null);
                  onForceResync(opt.opts);
                }}
                sx={{ borderRadius: '8px', mx: 0.5, my: 0.25, alignItems: 'flex-start', flexDirection: 'column' }}
              >
                <ListItemText
                  primary={opt.label}
                  secondary={opt.help}
                  primaryTypographyProps={{ sx: { fontSize: '13.5px', fontWeight: 600, color: 'text.primary' } }}
                  secondaryTypographyProps={{ sx: { fontSize: '12px', lineHeight: 1.45, color: 'text.secondary', whiteSpace: 'normal' } }}
                />
              </MenuItem>
            ))}
          </Menu>
        </Box>
      )}

      {manufacturerUrl && (
        <Button
          href={manufacturerUrl}
          target="_blank"
          rel="noopener noreferrer"
          endIcon={<OpenInNew sx={{ fontSize: 14 }} />}
          sx={outlinedActionSx}
        >
          Manufacturer site
        </Button>
      )}

      {/* Same gate as the AdminGuard on /app/admin/products (real admin), AND the
          effective role, so an admin impersonating a brand or store sees what that
          account sees and never a link its shell would bounce. */}
      {isPlatformAdmin && isAdmin && (
        <Button
          onClick={() => navigate(`/app/admin/products?productId=${encodeURIComponent(product.id)}`)}
          startIcon={<CategoryOutlined sx={{ fontSize: 16 }} />}
          sx={outlinedActionSx}
        >
          Open in Product Manager
        </Button>
      )}

      {moreItems.length > 0 && (
        <>
          <IconButton
            onClick={(e) => setMoreAnchor(e.currentTarget)}
            aria-label="More product actions"
            aria-haspopup="menu"
            sx={{
              width: 38,
              height: 38,
              borderRadius: '10px',
              border: '1px solid',
              borderColor: moreAnchor ? tokens.ink.disabled : 'divider',
              bgcolor: moreAnchor ? tokens.surface.tint : 'background.paper',
            }}
          >
            <MoreHoriz sx={{ fontSize: 18, color: 'text.secondary' }} />
          </IconButton>
          <Menu
            anchorEl={moreAnchor}
            open={Boolean(moreAnchor)}
            onClose={() => setMoreAnchor(null)}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
            transformOrigin={{ vertical: 'top', horizontal: 'left' }}
            PaperProps={{ sx: { width: 240, borderRadius: '12px', border: '1px solid', borderColor: 'divider', boxShadow: '0 12px 32px rgba(15,23,42,0.14)' } }}
          >
            {moreItems.map((item) => (
              <MenuItem
                key={item.label}
                disabled={item.disabled}
                onClick={() => {
                  setMoreAnchor(null);
                  item.onClick();
                }}
                sx={{ borderRadius: '8px', mx: 0.5, my: 0.25, fontSize: '13.5px', color: item.danger ? tokens.signal.problem.ink : 'text.primary' }}
              >
                {item.label}
              </MenuItem>
            ))}
          </Menu>
        </>
      )}
    </Box>
  );
}
